import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Car, LogOut, Loader2, Calendar, Wallet, ShieldCheck, FileText, User, CheckCircle2, Download, PenLine, Paperclip, X, Receipt, Archive } from "lucide-react";
import { apiFetch, openPdf, downloadFile } from "@/portal/api";
import { useAuth } from "@/portal/AuthContext";
import { SignaturePad } from "@/portal/SignaturePad";
import { toast } from "@/components/ui/sonner";

const LOGO_IMG = "/img/n148kmw9_image.webp";

const fmtCZK = (n) => (n == null ? "—" : `${Number(n).toLocaleString("cs-CZ")} Kč`);

const ClientPortal = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(null); // contract being signed
  const [signingProto, setSigningProto] = useState(null); // protocol being signed
  const [savingSig, setSavingSig] = useState(false);
  const [zipping, setZipping] = useState(false);

  const downloadAllInvoices = async () => {
    setZipping(true);
    try {
      await downloadFile("/portal/invoices/zip", token, "faktury.zip");
      toast.success("Faktury staženy (ZIP).");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setZipping(false);
    }
  };

  const load = async () => {
    try {
      const d = await apiFetch("/portal/overview", { token });
      setData(d);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const handleLogout = () => {
    logout();
    navigate("/prihlaseni", { replace: true });
  };

  const submitSignature = async (dataUrl) => {
    if (!signing) return;
    setSavingSig(true);
    try {
      await apiFetch(`/portal/contracts/${signing.id}/sign`, {
        method: "POST", token, body: { signature: dataUrl },
      });
      setSigning(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingSig(false);
    }
  };

  const submitProtocolSignature = async (dataUrl) => {
    if (!signingProto) return;
    setSavingSig(true);
    try {
      await apiFetch(`/portal/protocols/${signingProto.id}/sign`, {
        method: "POST", token, body: { signature: dataUrl },
      });
      setSigningProto(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingSig(false);
    }
  };

  const rental = data?.rentals?.[0];
  const contracts = data?.contracts || [];
  const invoices = data?.invoices || [];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="client-portal">
      {/* Header */}
      <header className="nav-glass sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_IMG} alt="EffectDeal" className="w-9 h-9 object-contain" />
            <div>
              <span className="font-['Cabinet_Grotesk'] font-bold tracking-wide block leading-tight">EFFECTDEAL</span>
              <span className="text-xs text-zinc-500">Klientská zóna</span>
            </div>
          </div>
          <button onClick={handleLogout} className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-2" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Odhlásit
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="font-['Cabinet_Grotesk'] text-2xl sm:text-3xl font-bold mb-1">
          Vítejte, {user?.name || user?.first_name || "kliente"} 👋
        </h1>
        <p className="text-zinc-500 mb-8">Přehled vašeho pronájmu a dokumentů</p>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-indigo-400 animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Active rental */}
            <div className="lg:col-span-2">
              <h2 className="text-sm uppercase tracking-wider text-indigo-400 font-semibold mb-3">Aktivní pronájem</h2>
              {rental ? (
                <div className="glass rounded-2xl p-6 sm:p-8" data-testid="active-rental">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                      <Car className="w-6 h-6 text-indigo-400" />
                    </div>
                    <div>
                      <p className="font-['Cabinet_Grotesk'] text-xl font-bold">
                        {rental.car ? `${rental.car.brand} ${rental.car.model}` : "Vozidlo"}
                      </p>
                      {rental.car && <p className="text-zinc-500 text-sm">SPZ: {rental.license_plate || "—"}</p>}
                    </div>
                    <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/20">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Aktivní
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-white/5 rounded-xl p-4 flex items-center gap-3">
                      <Wallet className="w-5 h-5 text-indigo-400" />
                      <div>
                        <p className="text-xs text-zinc-500">Měsíční nájemné</p>
                        <p className="font-medium">{fmtCZK(rental.amount)}</p>
                      </div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-indigo-400" />
                      <div>
                        <p className="text-xs text-zinc-500">Kauce</p>
                        <p className="font-medium">{fmtCZK(rental.deposit)}</p>
                      </div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-indigo-400" />
                      <div>
                        <p className="text-xs text-zinc-500">Od</p>
                        <p className="font-medium">{rental.date_from || "—"}</p>
                      </div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-indigo-400" />
                      <div>
                        <p className="text-xs text-zinc-500">Do</p>
                        <p className="font-medium">{rental.date_to || "—"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="glass rounded-2xl p-8 text-center text-zinc-500" data-testid="no-rental">
                  Aktuálně nemáte žádný aktivní pronájem.
                </div>
              )}

              {/* Documents */}
              <h2 className="text-sm uppercase tracking-wider text-indigo-400 font-semibold mb-3 mt-8">Dokumenty a smlouvy</h2>
              {contracts.length > 0 ? (
                <div className="space-y-2" data-testid="contracts-list">
                  {contracts.map((ct) => (
                    <div key={ct.id} className="glass rounded-2xl p-5" data-testid={`contract-${ct.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-indigo-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">Smlouva o nájmu vozidla</p>
                            <p className="text-xs text-zinc-500 truncate">
                              {ct.car_label}
                              {ct.status === "signed"
                                ? <span className="text-green-400"> · podepsáno</span>
                                : <span className="text-amber-400"> · čeká na váš podpis</span>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {ct.status !== "signed" && (
                            <button
                              onClick={() => setSigning(ct)}
                              className="btn-accent inline-flex items-center gap-2 text-sm"
                              data-testid={`contract-sign-${ct.id}`}
                            >
                              <PenLine className="w-4 h-4" /> Podepsat
                            </button>
                          )}
                          <button
                            onClick={() => openPdf(`/portal/contracts/${ct.id}/pdf`, token).catch((e) => toast.error(e.message))}
                            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 transition-colors"
                            data-testid={`contract-download-${ct.id}`}
                          >
                            <Download className="w-4 h-4" /> PDF
                          </button>
                        </div>
                      </div>

                      {ct.protocols && ct.protocols.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/5 space-y-2">
                          {["protocol", "handover", "return"].map((cat) => {
                            const items = ct.protocols.filter((p) => (p.category || "protocol") === cat);
                            if (items.length === 0) return null;
                            const label = cat === "handover" ? "Foto převzetí" : cat === "return" ? "Foto vrácení" : "Předávací protokoly";
                            return (
                              <div key={cat}>
                                <p className="text-xs text-zinc-500 mb-1">{label}</p>
                                <div className="flex flex-wrap gap-x-4 gap-y-2">
                                  {items.map((p) => (
                                    <div key={p.id} className="flex items-center gap-2">
                                      <button
                                        onClick={() => openPdf(`/portal/protocols/${p.id}/file`, token).catch((e) => toast.error(e.message))}
                                        className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white"
                                        data-testid={`protocol-view-${p.id}`}
                                      >
                                        <Paperclip className="w-3.5 h-3.5 text-indigo-400" /> {p.original_filename}
                                      </button>
                                      {p.is_generated && p.signed && (
                                        <span className="text-[11px] text-green-400 inline-flex items-center gap-1"><PenLine className="w-3 h-3" /> podepsáno</span>
                                      )}
                                      {p.is_generated && !p.signed && (
                                        <button
                                          onClick={() => setSigningProto(p)}
                                          className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/20 hover:bg-amber-500/25"
                                          data-testid={`protocol-sign-${p.id}`}
                                        >
                                          <PenLine className="w-3 h-3" /> Podepsat
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="glass rounded-2xl p-6 flex items-center gap-3 text-zinc-400" data-testid="documents-placeholder">
                  <FileText className="w-5 h-5 text-zinc-500" />
                  <span className="text-sm">Smlouva k podpisu a faktury se zde objeví, jakmile budou připraveny.</span>
                </div>
              )}

              {/* Invoices */}
              {invoices.length > 0 && (
                <>
                  <div className="flex items-center justify-between gap-3 mb-3 mt-8">
                    <h2 className="text-sm uppercase tracking-wider text-indigo-400 font-semibold">Faktury</h2>
                    <button
                      onClick={downloadAllInvoices}
                      disabled={zipping}
                      className="text-xs font-medium px-3 py-1.5 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-60"
                      data-testid="download-all-invoices-btn"
                      title="Stáhnout všechny faktury jako ZIP"
                    >
                      {zipping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
                      Stáhnout vše (ZIP)
                    </button>
                  </div>

                  {(() => {
                    const paidSum = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + (Number(i.amount) || 0), 0);
                    const dueSum = invoices.filter((i) => i.status !== "paid" && i.status !== "cancelled").reduce((s, i) => s + (Number(i.amount) || 0), 0);
                    return (
                      <div className="grid grid-cols-2 gap-3 mb-3" data-testid="payment-overview">
                        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3">
                          <p className="text-[11px] text-green-300">Zaplaceno</p>
                          <p className="font-bold text-green-300">{fmtCZK(paidSum)}</p>
                        </div>
                        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
                          <p className="text-[11px] text-amber-300">K úhradě</p>
                          <p className="font-bold text-amber-300">{fmtCZK(dueSum)}</p>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="space-y-2" data-testid="invoices-list">
                    {invoices.map((inv) => (
                      <div key={inv.id} className="glass rounded-2xl p-5 flex items-center justify-between gap-3" data-testid={`invoice-${inv.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                            <Receipt className="w-5 h-5 text-emerald-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">Faktura {inv.number}</p>
                            <p className="text-xs text-zinc-500 truncate">
                              {inv.invoice_type === "deposit" ? "Kauce" : "Nájemné"} · {fmtCZK(inv.amount)}
                              {inv.status === "paid"
                                ? <span className="text-green-400"> · zaplaceno</span>
                                : inv.status === "overdue"
                                  ? <span className="text-red-400"> · po splatnosti</span>
                                  : <span className="text-amber-400"> · k úhradě</span>}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => openPdf(`/portal/invoices/${inv.id}/pdf`, token).catch((e) => toast.error(e.message))}
                          className="btn-accent inline-flex items-center gap-2 text-sm flex-shrink-0"
                          data-testid={`invoice-download-${inv.id}`}
                        >
                          <Download className="w-4 h-4" /> PDF
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div>
              <h2 className="text-sm uppercase tracking-wider text-indigo-400 font-semibold mb-3">Moje údaje</h2>
              <div className="glass rounded-2xl p-6 space-y-3" data-testid="client-profile">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-indigo-400" />
                  </div>
                  <p className="font-medium">{user?.name}</p>
                </div>
                <Row label="E-mail" value={user?.email} />
                <Row label="Telefon" value={user?.phone} />
                <Row label="Datum narození" value={user?.birth_date} />
                <Row label="Adresa" value={user?.address} />
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Signature modal */}
      {signing && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="sign-modal">
          <div className="glass rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-['Cabinet_Grotesk'] text-xl font-bold">Podpis smlouvy</h3>
                <p className="text-xs text-zinc-500 mt-0.5">{signing.car_label}</p>
              </div>
              <button onClick={() => setSigning(null)} className="text-zinc-400 hover:text-white p-1" data-testid="sign-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-zinc-400 mb-4">
              Podpisem stvrzujete souhlas se smlouvou o nájmu vozidla. Elektronický podpis je závazný.
            </p>
            <SignaturePad onSave={submitSignature} onCancel={() => setSigning(null)} saving={savingSig} />
          </div>
        </div>
      )}

      {/* Protocol signature modal */}
      {signingProto && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="proto-sign-modal">
          <div className="glass rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-['Cabinet_Grotesk'] text-xl font-bold">Podpis protokolu</h3>
                <p className="text-xs text-zinc-500 mt-0.5">{signingProto.original_filename}</p>
              </div>
              <button onClick={() => setSigningProto(null)} className="text-zinc-400 hover:text-white p-1" data-testid="proto-sign-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-zinc-400 mb-4">
              Podpisem stvrzujete souhlas se stavem vozidla uvedeným v protokolu. Elektronický podpis je závazný.
            </p>
            <SignaturePad onSave={submitProtocolSignature} onCancel={() => setSigningProto(null)} saving={savingSig} />
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 text-sm border-t border-white/5 pt-3">
    <span className="text-zinc-500">{label}</span>
    <span className="text-zinc-200 text-right">{value || "—"}</span>
  </div>
);

export default ClientPortal;
